import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { useWindowHeight } from '@react-hook/window-size';
import { IconButton } from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';

import { BodySmallText, Heading4Text } from '../../typography/typography';
import AlxButton from '../../alx-components/button';
import { icons } from '../../../assets/icons/icons';
import { ModalContainerStyle } from './modal-container-style';

interface IModalContainer {
  title?: string;
  text?: string | React.JSX.Element;
  active: boolean;
  onClose: () => void;
  children?: any;
  buttons?: { onClick: () => void; className: string; label: string; dataCy?: string }[];
  className?: string;
  fullScreen?: boolean;
  dataCy?: string;
  clickLeft?: () => void;
  isResume?: boolean;
  contentDataCy?: string;
  required?: boolean;
  showLeftButton?: boolean;
  modalRef?: React.RefObject<HTMLDivElement>;
}

const ModalContainer: React.FC<IModalContainer> = ({
  title,
  text,
  active,
  children,
  buttons,
  className,
  fullScreen,
  dataCy,
  isResume,
  contentDataCy,
  required,
  showLeftButton = true,
  onClose,
  clickLeft,
}) => {
  const [windowHeight, setWindowHeight] = useState<number>(0);
  const targetRef: any = useRef<HTMLDivElement>(null);
  const onlyHeight = useWindowHeight();

  const handleResize = () => {
    setTimeout(() => {
      setWindowHeight(Math.max(onlyHeight, window.innerHeight));
    }, 0);
  };

  const resetOverflow = () => {
    document.body.style.overflow = 'initial';
  };

  useEffect(() => {
    if (active) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'initial';
    }
    handleResize();
    return resetOverflow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    return window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fullScreen && active) {
      disableBodyScroll(targetRef.current);
    } else if (active) {
      disableBodyScroll(targetRef.current);
    } else {
      enableBodyScroll(targetRef.current);
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [active, fullScreen]);

  return (
    <ModalContainerStyle
      height={windowHeight}
      className={classNames(className, { active: active, 'full-screen': fullScreen })}
      data-cy={dataCy}
    >
      <div style={{ height: windowHeight, minHeight: windowHeight }} className="modal-wrapper">
        <div className="modal-veil" onClick={() => (!required ? onClose() : null)} />
        <div className="modal-container">
          <div style={{ maxHeight: windowHeight }} className="modal-content" ref={targetRef} data-cy={contentDataCy}>
            {fullScreen ? (
              <div className="modal-navbar">
                <div className="modal-content-left">
                  {clickLeft && showLeftButton && (
                    <>
                      {isResume ? (
                        <AlxButton className="ghost" data-cy="reset-form" onClick={() => clickLeft()}>
                          {'Reiniciar solicitud'}
                        </AlxButton>
                      ) : (
                        <IconButton aria-label="back" onClick={() => clickLeft()}>
                          <KeyboardArrowLeft />
                        </IconButton>
                      )}
                    </>
                  )}
                </div>
                <div className="modal-content-right" onClick={() => onClose()}>
                  <IconButton aria-label="back">
                    <img src={icons.closeIcon} alt="" />
                  </IconButton>
                </div>
              </div>
            ) : (
              <div
                className="modal-content-line-wrapper"
                data-cy="modal-container-close-button"
                onClick={() => onClose()}
              >
                {!required && <div className="modal-content-line" />}
              </div>
            )}
            {(title || text) && (
              <div className="modal-top">
                {title && (
                  <div className="modal-title">
                    <Heading4Text>{title}</Heading4Text>
                  </div>
                )}
                {text && (
                  <div className="modal-text">
                    <BodySmallText>{text}</BodySmallText>
                  </div>
                )}
              </div>
            )}
            {children && <div className="modal-children">{children}</div>}
            {buttons && (
              <div className="modal-buttons">
                {buttons.map((item, index) => (
                  <div key={`modal-button-${index}`} className="modal-buttons__item">
                    <AlxButton data-cy={item.dataCy} className={item.className} onClick={() => item.onClick()}>
                      {item.label}
                    </AlxButton>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalContainerStyle>
  );
};

export default ModalContainer;
